import {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import HeaderTop from "../Components/HeaderTop";
import {showError, showSuccess} from "../utils/helper";
import {formatCardLogs, clearCardLogs, getCardLogs} from "../utils/cardPaymentLog";
import {formatErrorLogs, clearErrorLogs, getErrorLogs} from "../utils/errorLog";
import {sendAllLogsManual} from "../utils/logSender";
import user from "../Models/user";

const canViewLogs = () => {
    if (user.isAdmin()) return true
    try {
        const d = JSON.parse(localStorage.getItem('invoiceDevice') || '{}')
        return Array.isArray(d.permissions) && d.permissions.includes('logs.view')
    } catch {
        return false
    }
}

export default function Logs() {
    const navigate = useNavigate()
    const [authorized, setAuthorized] = useState(true)

    const [cardLogText, setCardLogText] = useState('')
    const [cardLogCount, setCardLogCount] = useState(0)
    const [errLogText, setErrLogText] = useState('')
    const [errLogCount, setErrLogCount] = useState(0)
    const [sendingLogs, setSendingLogs] = useState(false)

    useEffect(() => {
        if (!canViewLogs()) {
            setAuthorized(false)
            navigate('/', {replace: true})
            return
        }
        refreshAll()
    }, [navigate])

    const refreshCardLogs = () => {
        setCardLogText(formatCardLogs())
        setCardLogCount(getCardLogs().length)
    }

    const refreshErrLogs = () => {
        setErrLogText(formatErrorLogs())
        setErrLogCount(getErrorLogs().length)
    }

    const refreshAll = () => {
        refreshCardLogs()
        refreshErrLogs()
    }

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text)
            showSuccess('Log kopiran u međuspremnik')
        } catch (e) {
            showError('Kopiranje nije uspjelo')
        }
    }

    const downloadText = (text, prefix) => {
        const blob = new Blob([text], {type: 'text/plain;charset=utf-8'})
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${prefix}-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const handleClearCardLogs = () => {
        if (!window.confirm('Obrisati cijeli log kartičnih plaćanja?')) return
        clearCardLogs()
        refreshCardLogs()
        showSuccess('Log obrisan')
    }

    const handleClearErrLogs = () => {
        if (!window.confirm('Obrisati log grešaka aplikacije?')) return
        clearErrorLogs()
        refreshErrLogs()
        showSuccess('Log obrisan')
    }

    const handleSendLogs = async () => {
        if (sendingLogs) return
        setSendingLogs(true)
        try {
            const result = await sendAllLogsManual()
            if (result?.error) {
                showError('Slanje logova nije uspjelo: ' + result.error)
            } else if (result?.skipped) {
                showError('Slanje preskočeno (' + (result.reason || 'već u tijeku') + ')')
            } else {
                showSuccess(`Logovi poslani na server (${result?.sent ?? 0} zapisa).`)
            }
        } catch (e) {
            showError('Slanje logova nije uspjelo: ' + (e?.message || 'nepoznata greška'))
        } finally {
            setSendingLogs(false)
        }
    }

    if (!authorized) return null

    return (
        <>
            <HeaderTop title={'Logovi'}/>
            <div className="w-full h-full flex flex-column" style={{minHeight: `calc(100vh - 80px)`}}>
                <div className="w-full flex-grow flex">
                    <div className="flex flex-col bg-blue-gray-50 h-full w-full">
                        <div className="mt-4 px-2">

                            {/* Slanje na server */}
                            <div className="bg-white rounded-sm flex items-center justify-between shadow px-4 py-3 mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Pošalji logove na server</h3>
                                    <p className="text-sm text-gray-500">Šalje sve lokalne zapise (kartični + greške) na server radi dijagnostike. Ne šalje e-mail.</p>
                                </div>
                                <button
                                    onClick={handleSendLogs}
                                    disabled={sendingLogs}
                                    className="bg-primary hover:bg-yellow-600 text-black font-semibold py-2 px-4 rounded text-sm disabled:opacity-50">
                                    {sendingLogs ? 'Šaljem...' : 'Pošalji logove'}
                                </button>
                            </div>

                            {/* Kartična plaćanja */}
                            <div className="bg-white rounded-sm flex flex-col shadow px-4 py-4 mb-4">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <h3 className="text-2xl font-semibold leading-7 text-gray-900">Log kartičnih plaćanja</h3>
                                        <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Lokalni dijagnostički zapis (zadnjih do 500 događaja). {cardLogCount} zapisa.</p>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <button onClick={refreshCardLogs} className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-3 rounded text-sm">Osvježi</button>
                                        <button onClick={() => copyToClipboard(formatCardLogs())} className="bg-blue-600 hover:bg-blue-800 text-white py-2 px-3 rounded text-sm">Kopiraj</button>
                                        <button onClick={() => downloadText(formatCardLogs(), 'card-payment-log')} className="bg-green-600 hover:bg-green-800 text-white py-2 px-3 rounded text-sm">Preuzmi .txt</button>
                                        <button onClick={handleClearCardLogs} className="bg-red-600 hover:bg-red-800 text-white py-2 px-3 rounded text-sm">Obriši</button>
                                    </div>
                                </div>
                                <textarea
                                    readOnly
                                    value={cardLogText}
                                    className="mt-3 w-full h-72 font-mono text-xs p-2 border border-gray-200 rounded bg-gray-50"
                                    placeholder="(prazno)"
                                />
                            </div>

                            {/* Greške aplikacije */}
                            <div className="bg-white rounded-sm flex flex-col shadow px-4 py-4 mb-4">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <h3 className="text-2xl font-semibold leading-7 text-gray-900">Greške aplikacije</h3>
                                        <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Sve HTTP greške, prikazani toast erori i nehvatane JS iznimke. {errLogCount} zapisa.</p>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        <button onClick={refreshErrLogs} className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-3 rounded text-sm">Osvježi</button>
                                        <button onClick={() => copyToClipboard(formatErrorLogs())} className="bg-blue-600 hover:bg-blue-800 text-white py-2 px-3 rounded text-sm">Kopiraj</button>
                                        <button onClick={() => downloadText(formatErrorLogs(), 'app-error-log')} className="bg-green-600 hover:bg-green-800 text-white py-2 px-3 rounded text-sm">Preuzmi .txt</button>
                                        <button onClick={handleClearErrLogs} className="bg-red-600 hover:bg-red-800 text-white py-2 px-3 rounded text-sm">Obriši</button>
                                    </div>
                                </div>
                                <textarea
                                    readOnly
                                    value={errLogText}
                                    className="mt-3 w-full h-72 font-mono text-xs p-2 border border-gray-200 rounded bg-gray-50"
                                    placeholder="(prazno)"
                                />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
